<template>
  <div>
    <Button
      variant="text"
      icon="pi pi-pencil"
      size="small"
      @click="toggleOpen(props.taskId)"
    />

    <!-- Edit Task Dialog -->
    <Dialog
      v-model:visible="visible"
      header="Edit Task"
      :style="{ width: '600px' }"
      :modal="true"
    >
      <div
        v-if="isLoadingTask"
        class="flex justify-center items-center min-h-[200px]"
      >
        <ProgressSpinner />
      </div>

      <div
        v-else
        class="flex flex-col gap-4"
      >
        <Tabs value="0">
          <TabList>
            <Tab value="0">
              <div class="flex items-center gap-1">
                <i class="pi pi-pen-to-square" />
                Task
              </div>
            </Tab>

            <Tab value="1">
              <div class="flex items-center gap-1">
                <i class="pi pi-paperclip" />
                Attachments
              </div>
            </Tab>

            <Tab
              value="2"
            >
              <div class="flex items-center gap-1">
                <i class="pi pi-share-alt" />
                Associations
              </div>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <div class="grid grid-cols-4 gap-4 min-h-96">
                <div class="col-span-4">
                  <div class="flex flex-col gap-2">
                    <label for="content">Description
                      <span class="text-red-500">*</span></label>

                    <Textarea
                      v-model="taskData.description"
                      rows="5"
                      cols="30"
                    />
                  </div>
                </div>

                <div class="col-span-4">
                  <div class="flex flex-col gap-2">
                    <label for="content">Due Date
                      <span class="text-red-500">*</span></label>
                    <DatePicker
                      id="datepicker-24h"
                      v-model="taskData.dueDate"
                      :dateFormat="$t('util.dateFormatWithMonth') + ' -'"
                      showTime
                      hourFormat="12"
                      stepMinute="15"
                      fluid
                      showButtonBar
                    />
                  </div>
                </div>

                <div class="col-span-4">
                  <div class="flex flex-col gap-2">
                    <label for="content">Assigned To
                      <span class="text-red-500">*</span></label>
                    <Select
                      v-model="taskData.assignedTo"
                      :options="allUsers"
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select an agent"
                    />
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="1">
              <div class="h-80">
                <UploaderPublicStorage
                  ref="uploaderRef"
                  v-model:selectedFiles="stagedFiles"
                  v-model:uploadedFiles="uploadedFiles"
                  v-model:isSubmitting="isSubmitting"
                  v-model:filesPath="filesPath"
                />

                <div v-if="taskData.attachments?.length > 0">
                  <Divider />
                  <span>Files Attached:</span>
                  <div class="flex flex-col ">
                    <div
                      v-for="(item, index) in taskData.attachments"
                      :key="index"
                    >
                      <div class="flex flex-1 justify-between">
                        <div class="flex gap-2 items-center">
                          <i
                            class="pi pi-paperclip text-blue-500"
                            style="font-size: 0.9rem"
                          />
                          <a
                            :href="item.publicUrl"
                            class="hover:cursor-pointer text-blue-500"
                            target="_blank"
                          >
                            {{ item.name }}
                          </a>
                        </div>

                        <div>
                          <Button
                            icon="pi pi-pencil"
                            size="small"
                            variant="text"
                            @click="showEditAttachment(item)"
                          />

                          <Button
                            severity="danger"
                            icon="pi pi-trash"
                            size="small"
                            variant="text"
                            @click="removeAttachment(item.dropboxFileId)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="2">
              <div class="flex flex-col gap-4 h-80">
                <div
                  class="flex flex-col gap-2"
                >
                  <label for="type">
                    Contact
                    <span class="text-xs">[optional]</span>
                  </label>

                  <ContactSelectSingle
                    v-model:selectedContact="selectedContact"
                    :showCreateContact="false"
                    :isDisabled="taskData?.contactId ? true : false"
                  />
                </div>

                <div
                  class="flex flex-col gap-2"
                >
                  <label for="type">
                    Business
                    <span class="text-xs">[optional]</span>
                  </label>

                  <Select
                    v-model="selectedBusiness"
                    class="w-full"
                    :options="businesses"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select business"
                    filter
                    filterPlaceholder="Search businesses"
                    :disabled="taskData.businessId"
                  />
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2 px-5">
          <Button
            outlined
            label="Cancel"
            icon="pi pi-times"
            @click="hideDialog"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            :loading="loading"
            @click="saveTask"
          />
        </div>
      </template>
    </Dialog>

    <!-- Rename Attachment Dialog -->
    <Dialog
      v-model:visible="showEditModal"
      modal
      header="Rename file"
      :style="{ width: '25rem' }"
    >
      <div class="flex items-center gap-4 mb-4">
        <InputText
          v-model="editForm.name"
          placeholder="New File Name"
          class="flex-auto"
          autocomplete="off"
          fluid
        />
      </div>

      <div class="flex justify-end gap-2">
        <Button
          outlined
          label="Cancel"
          icon="pi pi-times"
          @click="showEditModal = false"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          :loading="isLoading"
          :disabled="isLoading"
          @click="updateAttachmentName(editForm.dropboxFileId)"
        />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { User } from '@prisma/client'

const props = defineProps<{
  taskId: string
  users: User[]
}>()

const emit = defineEmits(['refresh'])

const { getTaskById, updateTask, updateTaskAttachments } = useTasks()
const toast = useToast()
const route = useRoute()

// Edit Task Dialog
const visible = ref(false)

const toggleOpen = async (taskId: string) => {
  visible.value = true
  await loadTask(taskId)
}

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  resetForm()
}

// Rename Attachment Dialog
const showEditModal = ref(false)
const isLoading = ref(false)

const editForm = ref()

const showEditAttachment = (item) => {
  editForm.value = JSON.parse(JSON.stringify(item))
  showEditModal.value = true
}

const updateAttachmentName = async (attachmentFileId: string) => {
  if (!editForm.value.name?.trim()) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Invalid name',
      life: 3000,
    })
    return
  }

  isLoading.value = true

  const prevAttachments = [...taskData.value.attachments]

  const updatedAttachments = prevAttachments.map(item =>
    item.dropboxFileId === attachmentFileId ? { ...item, name: editForm.value.name.trim() } : item,
  )

  if (JSON.stringify(updatedAttachments) === JSON.stringify(prevAttachments)) {
    isLoading.value = false
    showEditModal.value = false
    return
  }

  await updateTaskAttachments(props.taskId, updatedAttachments)

  taskData.value.attachments = updatedAttachments

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'File name updated successfully',
    life: 3000,
  })

  showEditModal.value = false
  isLoading.value = false
}

const removeAttachment = async (attachmentFileId: string) => {
  if (confirm('Are you sure?')) {
    isLoading.value = true

    const prevAttachments = [...taskData.value.attachments]

    const updatedAttachments = prevAttachments.filter(
      item => item.dropboxFileId !== attachmentFileId,
    )

    if (updatedAttachments.length === prevAttachments.length) {
      isLoading.value = false
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to remove attachment',
        life: 3000,
      })
      return
    }

    await updateTaskAttachments(props.taskId, updatedAttachments)

    taskData.value.attachments = updatedAttachments

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'File removed successfully',
      life: 3000,
    })

    showEditModal.value = false
    isLoading.value = false
  }
}

// Form
const loading = ref(false)
const submitted = ref(false)

const taskData = ref({
  description: '',
  dueDate: '',
  assignedTo: '',
  isCompleted: '',
})

const errors = ref({
  description: false,
  dueDate: false,
})

const resetForm = () => {
  taskData.value = {
    description: '',
    dueDate: '',
    assignedTo: '',
    isCompleted: '',
  }

  errors.value = {
    description: false,
    dueDate: false,
  }
}

const validateForm = (): boolean => {
  errors.value.description = !taskData.value.description
  errors.value.dueDate = !taskData.value.dueDate

  return !Object.values(errors.value).some(Boolean)
}

// Files Uploader
const filesPath = ref('tasks')
const stagedFiles = ref([])
const uploaderRef = ref(null)
const isSubmitting = ref(false)
const uploadedFiles = ref<JSON[]>([])

const uploadAttachments = async () => {
  await uploaderRef.value?.uploadFiles()
}

// Users
const allUsers = ref()

// Associations
const { fetchContactLinkedBusinesses } = useContacts()
const { getAllPageAccessByBusinessId } = usePageAccess()
const contacts = ref()
const selectedContact = ref()

const { getAllBusinesses } = useBusiness()
const businesses = ref()
const selectedBusiness = ref()

// Load Task
const isLoadingTask = ref(false)

const loadTask = async (taskId: string) => {
  isLoadingTask.value = true

  try {
    const { data, error } = await getTaskById(taskId)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch task',
        life: 3000,
      })
      return
    }

    taskData.value = data
    taskData.value.dueDate = new Date (data.dueDate)
    selectedContact.value = data?.contactId
    selectedBusiness.value = data?.businessId

    // Load associations
    if (route.path.startsWith('/contacts/') && selectedContact.value) {
      const resLinkedBusinesses = await fetchContactLinkedBusinesses(selectedContact.value)
      businesses.value = resLinkedBusinesses.map((business: {
        Business: Business
      }) => {
        return {
          id: business.Business.id,
          name: business.Business.name,
        }
      })
    }
    else {
      const resBusinesses = await getAllBusinesses()
      businesses.value = resBusinesses
    }

    if (route.path.startsWith('/businesses/') && selectedBusiness.value) {
      const resPageAccess = await getAllPageAccessByBusinessId(selectedBusiness.value)
      contacts.value = resPageAccess.data?.filter(item => item.contactId).map((record) => {
        return {
          id: record.contactId,
          name: record.firstName + ' ' + record.lastName,
        }
      })
    }
  }
  catch (error) {
    console.error('Error fetching task:', error)
  }
  finally {
    isLoadingTask.value = false
  }
}

// Save Task
const saveTask = async () => {
  submitted.value = true

  const isValid = validateForm()

  if (errors.value.description && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Description is required',
      life: 3000,
    })
  }

  if (errors.value.dueDate && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Due date is required',
      life: 3000,
    })
  }

  if (!isValid) return

  loading.value = true

  try {
    const contactId = selectedContact.value
    const businessId = selectedBusiness.value

    const prevAttachments = [...taskData.value.attachments]

    const taskAttachments = ref<JSON[]>([])

    await uploadAttachments()

    // Check if any files were uploaded
    if (uploadedFiles.value.length > 0) {
      taskAttachments.value = [...prevAttachments, ...uploadedFiles.value]

      // Update task with new attachments
      const { data, error } = await updateTask(props.taskId, {
        description: taskData.value.description,
        dueDate: taskData.value.dueDate,
        assignedTo: taskData.value.assignedTo,
        attachments: taskAttachments.value,
      },
      contactId,
      businessId)

      if (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update task',
          life: 3000,
        })
        return
      }
    }
    else {
      // Update task content and keep previous attachments
      const { data, error } = await updateTask(props.taskId, {
        description: taskData.value.description,
        dueDate: taskData.value.dueDate,
        assignedTo: taskData.value.assignedTo,
      },
      contactId,
      businessId)

      if (error) {
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update task',
          life: 3000,
        })
        return
      }
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Task updated successfully',
      life: 3000,
    })

    hideDialog()
    emit('refresh')
  }
  catch (error) {
    console.error('Error updating task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update task',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}

onMounted(async () => {
  allUsers.value = props.users
})
</script>
